import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Save, Loader2, DollarSign, Users, TrendingUp,
  CheckCircle, Repeat, ArrowUpCircle, ArrowDownCircle, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCommissionStrategy() {
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Rate preview
  const [previewTier, setPreviewTier] = useState('pro');
  const [previewPartner, setPreviewPartner] = useState('affiliate');
  const [previewEvent, setPreviewEvent] = useState('first_purchase');

  const headers = useCallback(() => {
    const token = document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1];
    return { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
  }, []);

  useEffect(() => {
    fetch(`${API}/affiliate/admin/commission-strategy`, { headers: headers() })
      .then(r => r.json()).then(d => { setStrategy(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [headers]);

  const update = (key, value) => setStrategy(p => ({ ...p, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/affiliate/admin/commission-strategy`, {
        method: 'PATCH', headers: headers(), body: JSON.stringify(strategy),
      });
      if (res.ok) { setStrategy(await res.json()); toast.success('Strategy saved'); }
      else toast.error('Failed to save');
    } catch { toast.error('Error saving'); }
    setSaving(false);
  };

  const getPreviewRate = () => {
    if (!strategy) return 0;
    const tierRates = { basic: strategy.free_tier_commission_rate, pro: strategy.pro_tier_commission_rate, founder: strategy.founder_tier_commission_rate };
    const partnerRates = { affiliate: strategy.affiliate_base_rate, creator_partner: strategy.creator_partner_rate, strategic_partner: strategy.strategic_partner_rate };
    const tierRate = tierRates[previewTier] || 20;
    const partnerRate = partnerRates[previewPartner] || 20;
    if (strategy.rate_method === 'tier') return tierRate;
    if (strategy.rate_method === 'partner') return partnerRate;
    return Math.max(tierRate, partnerRate);
  };

  const getPreviewAmount = () => {
    const prices = { basic: 0, pro: 99, founder: 999 };
    const price = prices[previewTier] || 99;
    return (price * getPreviewRate() / 100).toFixed(2);
  };

  if (loading) return <div className="p-8 space-y-6">{[...Array(3)].map((_, i) => <div key={i} className="h-40 bg-gray-50 rounded-xl animate-pulse border border-gray-100" />)}</div>;
  if (!strategy) return <div className="p-8 text-gray-500">Unable to load commission strategy.</div>;

  const methods = [
    { id: 'tier', label: 'Use member-tier rates', desc: 'Commission based on the membership tier sold' },
    { id: 'partner', label: 'Use partner-type rates', desc: 'Commission based on the partner classification' },
    { id: 'highest', label: 'Use highest applicable rate', desc: 'Automatically applies the higher of tier or partner rate' },
  ];

  return (
    <div className="max-w-4xl mx-auto" data-testid="commission-strategy-page">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to="/admin"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Admin</Button></Link>
          <div>
            <h1 className="text-xl font-bold text-[#0f1f33]">Commission Strategy</h1>
            <p className="text-sm text-gray-400">Define how commissions are calculated.</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="save-strategy-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Strategy
        </Button>
      </div>

      <div className="space-y-6">
        {/* Section 1: Rate Method */}
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-50">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><DollarSign className="w-4.5 h-4.5 text-blue-600" /></div>
              <h3 className="font-semibold text-[#0f1f33]">Program Rate Method</h3>
            </div>
            <div className="space-y-3">
              {methods.map(m => (
                <label key={m.id} className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer border transition-all ${strategy.rate_method === m.id ? 'border-[#2563eb] bg-blue-50/30' : 'border-gray-100 hover:border-gray-200'}`}>
                  <input type="radio" name="rate_method" checked={strategy.rate_method === m.id} onChange={() => update('rate_method', m.id)}
                    className="w-4 h-4 text-[#2563eb]" data-testid={`method-${m.id}`} />
                  <div>
                    <p className="text-sm font-medium text-[#0f1f33]">{m.label}</p>
                    <p className="text-xs text-gray-400">{m.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Section 2 & 3: Rates */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-gray-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-50">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center"><TrendingUp className="w-4.5 h-4.5 text-emerald-600" /></div>
                <h3 className="font-semibold text-[#0f1f33]">Membership Tier Rates</h3>
              </div>
              {[
                { label: 'Free tier', field: 'free_tier_commission_rate' },
                { label: 'Pro tier', field: 'pro_tier_commission_rate' },
                { label: 'Founder tier', field: 'founder_tier_commission_rate' },
              ].map((r, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                  <span className="text-sm text-[#0f1f33]">{r.label}</span>
                  <div className="flex items-center gap-1">
                    <Input type="number" value={strategy[r.field]} onChange={e => update(r.field, parseFloat(e.target.value) || 0)} className="w-20 text-right" data-testid={`rate-${r.field}`} />
                    <span className="text-xs text-gray-400">%</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-gray-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-50">
                <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center"><Users className="w-4.5 h-4.5 text-violet-600" /></div>
                <h3 className="font-semibold text-[#0f1f33]">Partner Type Rates</h3>
              </div>
              {[
                { label: 'Affiliate', field: 'affiliate_base_rate' },
                { label: 'Creator Partner', field: 'creator_partner_rate' },
                { label: 'Strategic Partner', field: 'strategic_partner_rate' },
              ].map((r, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                  <span className="text-sm text-[#0f1f33]">{r.label}</span>
                  <div className="flex items-center gap-1">
                    <Input type="number" value={strategy[r.field]} onChange={e => update(r.field, parseFloat(e.target.value) || 0)} className="w-20 text-right" />
                    <span className="text-xs text-gray-400">%</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Section 4: Renewal & Upgrade Rules */}
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-50">
              <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center"><Repeat className="w-4.5 h-4.5 text-amber-600" /></div>
              <h3 className="font-semibold text-[#0f1f33]">Renewal & Upgrade Rules</h3>
            </div>
            {[
              { label: "Use original locked rate on renewals", desc: "Renewal commissions use the rate from the first conversion", field: "use_original_rate_on_renewals", icon: Repeat },
              { label: "Recalculate rate on upgrades", desc: "Upgrade commissions use the new tier's applicable rate", field: "recalculate_on_upgrades", icon: ArrowUpCircle },
              { label: "No commissions on downgrades", desc: "Downgrade events do not generate commissions", field: "no_commissions_on_downgrades", icon: ArrowDownCircle },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-3">
                  <item.icon className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-[#0f1f33]">{item.label}</p>
                    <p className="text-xs text-gray-400">{item.desc}</p>
                  </div>
                </div>
                <Switch checked={!!strategy[item.field]} onCheckedChange={v => update(item.field, v)} />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Section 6: Rate Preview */}
        <Card className="border-gray-100 shadow-sm border-2 border-blue-100">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-50">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Eye className="w-4.5 h-4.5 text-blue-600" /></div>
              <h3 className="font-semibold text-[#0f1f33]">Rate Preview Tool</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Tier Sold</label>
                <select value={previewTier} onChange={e => setPreviewTier(e.target.value)} className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm" data-testid="preview-tier">
                  <option value="basic">Free</option>
                  <option value="pro">Pro ($99/yr)</option>
                  <option value="founder">Founder ($999)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Partner Type</label>
                <select value={previewPartner} onChange={e => setPreviewPartner(e.target.value)} className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm" data-testid="preview-partner">
                  <option value="affiliate">Affiliate</option>
                  <option value="creator_partner">Creator Partner</option>
                  <option value="strategic_partner">Strategic Partner</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Event Type</label>
                <select value={previewEvent} onChange={e => setPreviewEvent(e.target.value)} className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm">
                  <option value="first_purchase">First Purchase</option>
                  <option value="renewal">Renewal</option>
                  <option value="upgrade">Upgrade</option>
                </select>
              </div>
            </div>
            <div className="flex gap-6 bg-[#f0f4ff] rounded-xl p-5">
              <div className="text-center flex-1">
                <p className="text-xs text-gray-500 mb-1">Applicable Rate</p>
                <p className="text-3xl font-bold text-[#2563eb]" data-testid="preview-rate">{getPreviewRate()}%</p>
                <p className="text-xs text-gray-400 mt-1 capitalize">Method: {strategy.rate_method}</p>
              </div>
              <div className="text-center flex-1 border-l border-blue-200 pl-6">
                <p className="text-xs text-gray-500 mb-1">Sample Commission</p>
                <p className="text-3xl font-bold text-emerald-600" data-testid="preview-amount">${getPreviewAmount()}</p>
                <p className="text-xs text-gray-400 mt-1">on {previewTier === 'basic' ? '$0' : previewTier === 'pro' ? '$99' : '$999'} sale</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
